import React, { useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import NotificationBell from './NotificationBell';
import AccountMenu from './AccountMenu';

const getTimeContext = () => {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay(); // 0=Sun 1=Mon … 6=Sat

    let period;
    if (hour >= 5 && hour < 12) period = 'morning';
    else if (hour >= 12 && hour < 17) period = 'afternoon';
    else if (hour >= 17 && hour < 21) period = 'evening';
    else period = 'night';

    const emojis = { morning: '🌅', afternoon: '☀️', evening: '🌆', night: '🌙' };
    const greetings = { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening', night: 'Good evening' };

    // day keys: 0=sunday,1=monday,5=friday,6=saturday, rest=weekday
    const dayKey =
        day === 1 ? 'monday' :
        day === 5 ? 'friday' :
        day === 6 ? 'saturday' :
        day === 0 ? 'sunday' :
        'weekday';

    const subtitles = {
        morning: {
            monday:   "New week, make it count.",
            friday:   "Last push of the week — finish strong.",
            saturday: "Enjoy your morning off.",
            sunday:   "Recharge before the week ahead.",
            weekday:  "Ready to make today count?",
        },
        afternoon: {
            monday:   "Good start — keep the momentum.",
            friday:   "Almost the weekend, stay focused.",
            saturday: "Hope you're enjoying your Saturday.",
            sunday:   "Make the most of the rest of your Sunday.",
            weekday:  "Keep the momentum going.",
        },
        evening: {
            monday:   "Solid first day of the week.",
            friday:   "The weekend is here — enjoy it!",
            saturday: "Hope today was a great one.",
            sunday:   "Rest up, big week ahead.",
            weekday:  "Wrapping up for the day?",
        },
        night: {
            monday:   "Early week, but rest is important.",
            friday:   "Enjoy the weekend ahead.",
            saturday: "Night owl mode on.",
            sunday:   "Prepare for a great week ahead.",
            weekday:  "Burning the midnight oil?",
        },
    };

    const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

    return {
        emoji: emojis[period],
        greeting: greetings[period],
        subtitle: subtitles[period][dayKey],
        dateStr,
    };
};

const TopBar = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const firstName = localStorage.getItem('FIRST_NAME') || '';

    const [ctx, setCtx] = useState(getTimeContext);

    // Recalculate at the top of every hour
    useEffect(() => {
        const now = new Date();
        const msToNextHour = (60 - now.getMinutes()) * 60 * 1000 - now.getSeconds() * 1000;
        const timeout = setTimeout(() => {
            setCtx(getTimeContext());
        }, msToNextHour);
        return () => clearTimeout(timeout);
    }, [ctx]);

    return (
        <Box
            sx={{
                position: 'relative',
                zIndex: isMobile ? 10 : 'unset',
                backgroundColor: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                px: { xs: 2, sm: 3 },
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
            }}
        >
            <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2c3e50', lineHeight: 1.3 }}>
                    {ctx.emoji}&nbsp;{ctx.greeting}{firstName ? `, ${firstName}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f8c9a', mt: 0.25 }}>
                    {ctx.subtitle}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                    variant="body2"
                    sx={{ color: '#7f8c9a', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}
                >
                    {ctx.dateStr}
                </Typography>
                <NotificationBell />
                <AccountMenu />
            </Box>
        </Box>
    );
};

export default TopBar;
